import { Map } from 'immutable';

import { StringResult } from 'common/stringresult';
import { Serializer } from 'common/serializer';
import { IDisposable } from 'common/disposable';
import { Observable, AsyncSubject, Subject } from 'common/barrels/rx';
import { IResourceProvider } from 'common/resourceprovider';
import { Request } from 'common/request';

import * as path from 'shared/path';

const typeRegistry = Map<string, typeof Object>().withMutations(map => {
    const register = (type: any, name: string) => map.set(name, type);
    register(StringResult, 'Web.StringResult');
});

const serializer = new Serializer(typeRegistry);

class NamespaceStrings implements IDisposable {
    private cancel = new Subject<{}>();

    private resources: AsyncSubject<{}>;

    constructor(source: string, namespace: string, culture: string, version: string) {
        this.resources = new AsyncSubject<{}>();

        Request.create(serializer, path.publicResources(`strings?s=${source}&n=${namespace}&c=${culture || ''}&v=${version}`))
            .handle(StringResult, x => {
                this.resources.next(JSON.parse(x.value));
                this.resources.complete();
            })
            .get(this.cancel)
            ;
    }

    getString(key: string): Observable<string> {
        return this.resources.map(x => x[key]);
    }

    dispose(): void {
        this.resources = null;
        this.cancel.next({});
    }
}

export class ResourceProvider implements IResourceProvider, IDisposable {

    private resourcesVersion: string;

    private coreResources: object;
    private formats: object;
    private systemWorkTrayViews: object;
    private bulkActions: object;

    private culture: string;

    private cache: Map<string, NamespaceStrings>;

    constructor(core: string, version: string, culture: string) {
        const all = JSON.parse(core);

        this.coreResources = all['Core'];
        this.formats = all['Formats'];
        this.systemWorkTrayViews = all['SystemWorkTrayViews'];
        this.bulkActions = all['BulkActions'];
        this.resourcesVersion = version;
        this.culture = culture;
        this.cache = Map<string, NamespaceStrings>();
    }

    dispose(): void {
        this.cache.forEach(x => { x.dispose(); });
    }

    private cacheKey(source: string, namespace: string) {
        return `${source}.${namespace}`;
    }

    getString(definitionName: string, languageKey: string): Observable<string> {
        const parts = languageKey.split('.');

        if (parts.length < 2)
            throw new Error(`Language key ${languageKey} has fewer than two parts!`);

        const source = parts.length > 2 ? parts.slice(0, parts.length - 2).join('.') : definitionName;
        const namespace = parts[parts.length - 2];
        const key = parts[parts.length - 1];

        const cacheKey = this.cacheKey(source, namespace);

        if (!this.cache.has(cacheKey))
            this.cache = this.cache.set(cacheKey, new NamespaceStrings(source, namespace, this.culture, this.resourcesVersion));

        return this.cache.get(cacheKey).getString(key);
    }

    getSystemWorkTrayViewString(key: string) {
        return this.systemWorkTrayViews[key];
    }

    getBulkActionString(key: string) {
        return this.bulkActions[key];
    }

    getCoreString(key: string, ...args: any[]): string {
        const s = this.coreResources[key];
        return args && args.length > 0 ? this.format(s, ...args) : s;
    }

    getFormat(formatName: string): string {
        return this.formats[formatName];
    }

    version() {
        return this.resourcesVersion;
    }

    private format(s: string, ...args: any[]): string {
        if (!args || args.length === 0)
            return s;

        return s.replace(/(\{(\d+?)\})/g, ($0, $1, $2) => {
            var i = parseInt($2, 10);
            if (i < args.length) {
                return args[i];
            } else {
                throw 'The number indicating an argument to be formatted (' + i + ') is larger than or equal to the number of the format arguments.';
            }
        });
    }

    makeList(items: string[]): string {
        return items.join(this.getFormat('ListSeparator') + ' ');
    }

    dateTimeFormat(): string {
        return this.dateFormat() + ' ' + this.timeFormat();
    }

    dateFormat(): string {
        return this.getFormat('Date').replace(/d/g, 'D').replace(/yyyy/g, 'YYYY');
    }

    timeFormat(): string {
        return this.getFormat('Time').replace(/tt/g, 'A');
    }
}
